// Utility centralisée d'affichage. Toutes les conversions centimes/centi-m²/pct → texte se font ici.

const FORMATTER_EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const FORMATTER_NUMBER_2 = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const FORMATTER_DATE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const FORMATTER_DATETIME = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Paris',
});

// Montant en centimes → "123,45 €".
export function formatEuro(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return '—';
  return FORMATTER_EUR.format(centimes / 100);
}

// Surface en centi-m² → "75,00 m²".
export function formatM2(centiM2: number | null | undefined): string {
  if (centiM2 === null || centiM2 === undefined) return '—';
  return `${FORMATTER_NUMBER_2.format(centiM2 / 100)} m²`;
}

// Pourcentage en centièmes de % → "20,00 %".
export function formatPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return '—';
  return `${FORMATTER_NUMBER_2.format(pct / 100)} %`;
}

// Date ISO UTC → "JJ/MM/AAAA" (Europe/Paris).
export function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  try {
    return FORMATTER_DATE.format(new Date(iso));
  } catch {
    return '—';
  }
}

// Date ISO UTC → "JJ/MM/AAAA HH:MM" (Europe/Paris).
export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  try {
    return FORMATTER_DATETIME.format(new Date(iso));
  } catch {
    return '—';
  }
}

// Convertit un nombre saisi (français "1 234,56" ou "1234.56") en centimes.
// null si invalide ou vide.
export function parseEuroToCentimes(input: string): number | null {
  const cleaned = input.trim().replace(/€/g, '').replace(/\s/g, '').replace(/,/g, '.');
  if (cleaned === '') return null;
  const f = Number.parseFloat(cleaned);
  if (!Number.isFinite(f) || f < 0) return null;
  return Math.round(f * 100);
}

// Idem pour m² → centi-m².
export function parseM2ToCentiM2(input: string): number | null {
  return parseEuroToCentimes(input);
}

// Idem pour pourcentage → centièmes de % (20 → 2000, "20,5" → 2050).
export function parsePctToCentiemes(input: string): number | null {
  const cleaned = input.trim().replace(/%/g, '').replace(/\s/g, '').replace(/,/g, '.');
  if (cleaned === '') return null;
  const f = Number.parseFloat(cleaned);
  if (!Number.isFinite(f) || f < 0 || f > 99.99) return null;
  return Math.round(f * 100);
}

// Convertit une date saisie "JJ/MM/AAAA" ou input type="date" "AAAA-MM-JJ" en ISO UTC YYYY-MM-DDT00:00:00.000Z.
export function parseDateToIso(input: string): string | null {
  const v = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00.000Z`;
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    return `${m[3] ?? ''}-${m[2] ?? ''}-${m[1] ?? ''}T00:00:00.000Z`;
  }
  return null;
}

// Convertit une date ISO UTC en "YYYY-MM-DD" pour <input type="date" />.
export function isoToDateInput(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '';
  return iso.slice(0, 10);
}

// Labels d'affichage pour les enums (les valeurs stockées sont sans accent).
export const LABEL_TYPE_CLIENT: Record<string, string> = {
  Paysagiste: 'Paysagiste',
  Negoce: 'Négoce',
  Camping: 'Camping',
  Distributeur: 'Distributeur',
  Particulier: 'Particulier',
  Autre: 'Autre',
};

export const LABEL_STATUT_PAIEMENT: Record<string, string> = {
  Payee: 'Payée',
  En_attente: 'En attente',
  En_retard: 'En retard',
};

export const LABEL_MODE_PAIEMENT: Record<string, string> = {
  Virement: 'Virement',
  Cheque: 'Chèque',
  Especes: 'Espèces',
};

export const LABEL_TERRITOIRE_USER: Record<string, string> = {
  Nord_Vendee_44: 'Nord Vendée + 44',
  Sud_Vendee_17: 'Sud Vendée + 17',
  Les_deux: 'Les deux',
};

export const LABEL_TERRITOIRE_CONTACT: Record<string, string> = {
  Adam: 'Adam',
  Matteo: 'Mattéo',
  Les_deux: 'Les deux',
};

export const LABEL_SOURCE_CONTACT: Record<string, string> = {
  Salon: 'Salon',
  BAO: 'Bouche-à-oreille',
  Web: 'Web',
  Prospection: 'Prospection',
  LeBonCoin: 'Le Bon Coin',
  Autre: 'Autre',
};

export const LABEL_STATUT_PIPELINE: Record<string, string> = {
  Prospect: 'Prospect',
  Contacte: 'Contacté',
  Devis_envoye: 'Devis envoyé',
  Negociation: 'Négociation',
  Gagne: 'Gagné',
  Perdu: 'Perdu',
  En_pause: 'En pause',
};

export const LABEL_STATUT_ECHEANCE: Record<string, string> = {
  En_attente: 'En attente',
  Payee: 'Payée',
  En_retard: 'En retard',
  Partielle: 'Partielle',
};

export const LABEL_TYPE_AVOIR: Record<string, string> = {
  Credit: 'Crédit',
  Remboursement: 'Remboursement',
};

export const LABEL_STATUT_AVOIR: Record<string, string> = {
  Emis: 'Émis',
  Utilise: 'Utilisé',
  Rembourse: 'Remboursé',
  Annule: 'Annulé',
};

export const LABEL_CATEGORIE_CHARGE: Record<string, string> = {
  Loyer: 'Loyer',
  Salaires: 'Salaires',
  Cotisations_sociales: 'Cotisations sociales',
  Comptable: 'Comptable',
  Telecom: 'Télécom',
  Assurance: 'Assurance',
  Carburant: 'Carburant',
  Abonnements_logiciels: 'Abonnements logiciels',
  Frais_bancaires: 'Frais bancaires',
  Autres: 'Autres',
};

// Format mois "YYYY-MM" en libellé français "mai 2026".
const MOIS_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];
export function formatMois(yyyymm: string): string {
  if (yyyymm.length < 7) return yyyymm;
  const an = yyyymm.slice(0, 4);
  const mois = Number.parseInt(yyyymm.slice(5, 7), 10);
  if (!Number.isFinite(mois) || mois < 1 || mois > 12) return yyyymm;
  return `${MOIS_FR[mois - 1] ?? ''} ${an}`;
}
